/**
 * Sample Da Vinci PAS Bundle aligned with prior-auth-service PAS scenarios
 * and Config/X12/Transactions/278/FhirMappings.json.
 * Values match BINDING_SAMPLE_VALUES / Generated EDI preview where applicable.
 */
import {
  PAS_FHIR_URLS,
  pasFullUrl,
  pasSupportingInfoCategory,
} from "./pasFhirUrls";

export const FHIR_SAMPLE_INTRO =
  "Sample PAS Bundle using backend CodeSystems and supportingInfo shapes. Envelope/control numbers are client config, not FHIR.";

const IDS = {
  claim: "AUTH123456",
  patient: "patient-subscriber-01",
  coverage: "coverage-subscriber-01",
  requesterOrg: "organization-requester-01",
  payerOrg: "organization-payer-01",
  careTeamPractitioner: "practitioner-careteam-01",
};

const claimResource = {
  resourceType: "Claim",
  id: IDS.claim,
  meta: {
    profile: [PAS_FHIR_URLS.CLAIM_PROFILE],
  },
  status: "active",
  identifier: [
    {
      system: PAS_FHIR_URLS.PRIOR_AUTH_ID,
      value: IDS.claim,
    },
  ],
  type: {
    coding: [
      {
        system: PAS_FHIR_URLS.CLAIM_TYPE,
        code: "professional",
      },
    ],
  },
  use: "preauthorization",
  patient: { reference: `Patient/${IDS.patient}` },
  created: "2025-07-22T14:30:00-05:00",
  provider: { reference: `Organization/${IDS.requesterOrg}` },
  insurer: { reference: `Organization/${IDS.payerOrg}` },
  insurance: [
    {
      sequence: 1,
      focal: true,
      coverage: { reference: `Coverage/${IDS.coverage}` },
    },
  ],
  priority: {
    coding: [
      {
        system: PAS_FHIR_URLS.PROCESS_PRIORITY,
        code: "normal",
        display: "Normal",
      },
    ],
  },
  careTeam: [
    {
      sequence: 1,
      extension: [
        {
          url: PAS_FHIR_URLS.CARE_TEAM_CLAIM_SCOPE,
          valueBoolean: true,
        },
      ],
      provider: { reference: `Practitioner/${IDS.careTeamPractitioner}` },
      role: {
        coding: [
          {
            system: PAS_FHIR_URLS.CLAIM_CARE_TEAM_ROLE,
            code: "rendering",
          },
        ],
        text: "Requesting Provider",
      },
      qualification: {
        coding: [
          {
            system: PAS_FHIR_URLS.NUCC_TAXONOMY,
            code: "207Q00000X",
            display: "Family Medicine",
          },
        ],
      },
    },
  ],
  supportingInfo: [
    {
      sequence: 1,
      category: pasSupportingInfoCategory("certificationAction", "Certification Action"),
      code: {
        coding: [{ code: "I", display: "Initial" }],
      },
    },
  ],
  diagnosis: [
    {
      sequence: 1,
      diagnosisCodeableConcept: {
        coding: [
          {
            system: PAS_FHIR_URLS.ICD10_CM,
            code: "M54.5",
            display: "Low back pain",
          },
        ],
      },
    },
  ],
  item: [
    {
      sequence: 1,
      category: {
        coding: [
          {
            system: PAS_FHIR_URLS.CERTIFICATION_TYPE_CS,
            code: "HS",
            display: "Health Services Review",
          },
        ],
      },
      productOrService: {
        coding: [
          {
            system: PAS_FHIR_URLS.CPT,
            code: "99213",
            display: "Office visit",
          },
        ],
        text: "Office visit",
      },
      servicedPeriod: {
        start: "2025-07-22",
        end: "2025-07-22",
      },
      locationCodeableConcept: {
        coding: [
          {
            system: PAS_FHIR_URLS.CMS_POS,
            code: "11",
            display: "Office",
          },
        ],
      },
      quantity: {
        value: 1,
      },
      unitPrice: {
        value: 150.0,
        currency: "USD",
      },
      net: {
        value: 150.0,
        currency: "USD",
      },
      careTeamSequence: [1],
    },
  ],
};

const patientResource = {
  resourceType: "Patient",
  id: IDS.patient,
  identifier: [
    {
      system: "https://payer.example/memberid",
      value: "MEM123456789",
    },
  ],
  name: [
    {
      family: "DOE",
      given: ["JOHN", "M"],
    },
  ],
  birthDate: "1985-03-15",
  gender: "male",
  address: [
    {
      line: ["456 OAK AVE", "APT 2B"],
      city: "PHILADELPHIA",
      state: "PA",
      postalCode: "19103",
    },
  ],
};

const coverageResource = {
  resourceType: "Coverage",
  id: IDS.coverage,
  status: "active",
  subscriberId: "MEM123456789",
  beneficiary: { reference: `Patient/${IDS.patient}` },
  subscriber: { reference: `Patient/${IDS.patient}` },
  payor: [{ reference: `Organization/${IDS.payerOrg}` }],
  class: [
    {
      type: {
        coding: [
          {
            system: PAS_FHIR_URLS.COVERAGE_CLASS,
            code: "plan",
          },
        ],
      },
      value: "60054",
      name: "AETNA HEALTH",
    },
  ],
};

const requesterOrganization = {
  resourceType: "Organization",
  id: IDS.requesterOrg,
  identifier: [
    {
      system: PAS_FHIR_URLS.NPI,
      value: "1234567890",
    },
  ],
  name: "VALLEY MEDICAL GROUP",
  telecom: [
    {
      system: "phone",
      value: "5551234567",
      use: "work",
    },
  ],
  address: [
    {
      line: ["123 MAIN ST", "SUITE 200"],
      city: "ANYTOWN",
      state: "PA",
      postalCode: "19087",
    },
  ],
};

const careTeamPractitioner = {
  resourceType: "Practitioner",
  id: IDS.careTeamPractitioner,
  identifier: [
    {
      system: PAS_FHIR_URLS.NPI,
      value: "1234567890",
    },
  ],
  name: [
    {
      family: "VALLEY",
      given: ["ROBERT", "A"],
    },
  ],
  qualification: [
    {
      code: {
        coding: [
          {
            system: PAS_FHIR_URLS.NUCC_TAXONOMY,
            code: "207Q00000X",
          },
        ],
      },
    },
  ],
};

const payerOrganization = {
  resourceType: "Organization",
  id: IDS.payerOrg,
  identifier: [
    {
      system: PAS_FHIR_URLS.NPI,
      value: "60054",
    },
  ],
  name: "AETNA HEALTH",
};

export const FHIR_SAMPLE_RESOURCES = {
  Claim: claimResource,
  Patient: patientResource,
  Coverage: coverageResource,
  Organization: payerOrganization,
  RequesterOrganization: requesterOrganization,
  Practitioner: careTeamPractitioner,
};

export const FHIR_SAMPLE_BUNDLE = {
  resourceType: "Bundle",
  type: "collection",
  entry: [
    {
      fullUrl: pasFullUrl("Claim", IDS.claim),
      resource: claimResource,
    },
    {
      fullUrl: pasFullUrl("Patient", IDS.patient),
      resource: patientResource,
    },
    {
      fullUrl: pasFullUrl("Coverage", IDS.coverage),
      resource: coverageResource,
    },
    {
      fullUrl: pasFullUrl("Organization", IDS.requesterOrg),
      resource: requesterOrganization,
    },
    {
      fullUrl: pasFullUrl("Organization", IDS.payerOrg),
      resource: payerOrganization,
    },
    {
      fullUrl: pasFullUrl("Practitioner", IDS.careTeamPractitioner),
      resource: careTeamPractitioner,
    },
  ],
};

export const FHIR_RESOURCE_TABS = [
  { id: "bundle", label: "Full Bundle" },
  { id: "Claim", label: "Claim" },
  { id: "Patient", label: "Patient" },
  { id: "Coverage", label: "Coverage" },
  { id: "RequesterOrganization", label: "Requester (Org)" },
  { id: "Practitioner", label: "Care Team" },
  { id: "Organization", label: "Payer (Org)" },
];
